/**
 * Centralized authentication error handling utility
 * Provides consistent error detection and redirect logic across the application
 */

import {
  removeToken,
  removeUser,
  clearOriginalAdmin,
  getToken,
} from "@/app/services/utils";
import { getLoginPathForRoute } from "@/app/lib/auth/login-routes";

/**
 * Error patterns that indicate authentication failures
 * Covers all known JWT verification errors from jose library and server responses
 */
const AUTH_ERROR_PATTERNS = [
  // Token presence errors (often client-side before cookie hydrate)
  /no authentication token found/i,
  /authentication token not found/i,
  /no authorization header/i,
  /no authorization token/i,
  /empty token/i,
  // JWT verification errors (jose library)
  /invalid token/i,
  /invalid compact jws/i,
  /invalid signature/i,
  /jwt malformed/i,
  /jwt expired/i,
  /jws verification/i,
  /verification failed/i,
  // Token validity errors
  /expired token/i,
  /token.*expired/i,
  /token.*invalid/i,
  // General auth errors (do not match /authorization/i — 403 bodies often say "Admin access required")
  /authentication required/i,
  /unauthorized/i,
  /\b401\b/,
  /mfa required/i,
  /missing mfa/i,
  // User state errors
  /user not found/i,
  /user.*inactive/i,
];

/** Client-side missing storage token — cookie may still be valid; try hydrate first. */
const LOCAL_TOKEN_MISSING_PATTERNS = [
  /no authentication token found/i,
  /authentication token not found/i,
  /authentication token is missing/i,
  /authentication token is required/i,
  /authentication required/i,
  /no authorization header/i,
  /no authorization token/i,
  /empty token/i,
];

/** Server confirmed the JWT/cookie is bad — safe to clear cookie. */
const CONFIRMED_INVALID_SESSION_PATTERNS = [
  /invalid token/i,
  /invalid compact jws/i,
  /invalid signature/i,
  /jwt malformed/i,
  /jwt expired/i,
  /jws verification/i,
  /verification failed/i,
  /expired token/i,
  /token.*expired/i,
  /token.*invalid/i,
  /mfa required/i,
  /missing mfa/i,
  /user not found/i,
  /user.*inactive/i,
];

function errorMessageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return String(error ?? "");
}

/**
 * Checks if an error is authentication-related
 * @param error - The error to check (Error object or string)
 * @returns true if the error is authentication-related
 */
/** HTTP 401 only — use for fetch/React Query errors; 403 is permission denied, not logout. */
export function isHttpSessionAuthError(error: unknown): boolean {
  if (error && typeof error === "object") {
    const err = error as { status?: number; response?: { status?: number } };
    if (err.status === 401) return true;
    if (err.response?.status === 401) return true;
  }
  return false;
}

export function isLocalTokenMissingError(error: unknown): boolean {
  const message = errorMessageOf(error);
  return LOCAL_TOKEN_MISSING_PATTERNS.some((pattern) => pattern.test(message));
}

export function isConfirmedInvalidSessionError(error: unknown): boolean {
  const message = errorMessageOf(error);
  return CONFIRMED_INVALID_SESSION_PATTERNS.some((pattern) =>
    pattern.test(message)
  );
}

export function isAuthError(error: Error | string | unknown): boolean {
  if (!error) return false;
  if (error instanceof AuthenticationError) return true;
  if (error instanceof PermissionError) return false;

  return AUTH_ERROR_PATTERNS.some((pattern) =>
    pattern.test(errorMessageOf(error))
  );
}

export type ClearAuthOptions = {
  /**
   * When true, POST /api/authentication/logout to delete the HttpOnly cookie.
   * Only use for server-confirmed invalid/expired sessions — never for a
   * locally-missing sessionStorage token in one tab (cookie may still be valid).
   */
  clearServerSession?: boolean;
};

/**
 * Clears authentication data (token, user, impersonation) from browser storage.
 * Optionally clears the HttpOnly session cookie.
 */
function clearAuthData(options: ClearAuthOptions = {}): void {
  const { clearServerSession = false } = options;
  console.log("[auth-error-handler] Clearing authentication data", {
    clearServerSession,
  });
  removeToken();
  removeUser();
  clearOriginalAdmin();
  if (clearServerSession && typeof window !== "undefined") {
    void fetch("/api/authentication/logout", { method: "POST" }).catch(() => {
      /* non-blocking cookie clear */
    });
  }
}

/**
 * Stores the current URL in sessionStorage for post-login redirect
 */
function storeRedirectTarget(): void {
  if (typeof window === "undefined") return;

  try {
    const currentUrl = window.location.href;
    sessionStorage.setItem("post_login_redirect", currentUrl);
    console.log("[auth-error-handler] Stored redirect target:", currentUrl);
  } catch (error) {
    console.warn("[auth-error-handler] Failed to store redirect target:", error);
  }
}

/**
 * Redirects to the appropriate login page based on the current route.
 * Uses window.location.href as a fallback (works in service layer).
 */
function performRedirect(redirectTo?: string): void {
  if (typeof window === "undefined") {
    console.warn("[auth-error-handler] Cannot redirect on server-side");
    return;
  }

  const target = redirectTo ?? getLoginPathForRoute();
  console.log("[auth-error-handler] Redirecting to:", target);
  window.location.href = target;
}

/**
 * Clears authentication data and redirects to the appropriate login page.
 * By default does NOT delete the HttpOnly cookie unless clearServerSession is set.
 */
export function clearAuthAndRedirect(
  redirectTo?: string,
  options: ClearAuthOptions = {}
): void {
  console.log("[auth-error-handler] Handling authentication error");
  clearAuthData(options);
  storeRedirectTarget();
  performRedirect(redirectTo);
}

let hydrateInFlight: Promise<boolean> | null = null;

/**
 * Attempt to restore storage token from the shared HttpOnly cookie via /check.
 * Dedupes concurrent callers (e.g. many React Query failures in one paint).
 */
export async function attemptSessionHydrate(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (getToken()) return true;

  if (!hydrateInFlight) {
    hydrateInFlight = (async () => {
      try {
        const { authService } = await import("@/app/services/authService");
        const user = await authService.getCurrentUser();
        return Boolean(user && getToken());
      } catch (error) {
        console.warn("[auth-error-handler] Session hydrate failed:", error);
        return false;
      } finally {
        hydrateInFlight = null;
      }
    })();
  }

  return hydrateInFlight;
}

/**
 * Main handler for authentication errors
 * Detects auth errors, clears auth data, and redirects
 * @param error - The error that occurred (Error object or string)
 * @param redirectTo - Optional custom redirect URL (defaults to role-aware login)
 * @returns true if the error was handled as an auth error
 */
export function handleAuthError(
  error: Error | string | unknown,
  redirectTo?: string
): boolean {
  if (!isAuthError(error)) {
    return false;
  }

  const errorMessage = errorMessageOf(error);
  console.log("[auth-error-handler] Authentication error detected:", errorMessage);

  // Local missing token: try cookie hydrate asynchronously; do not wipe cookie.
  if (isLocalTokenMissingError(error) && !isConfirmedInvalidSessionError(error)) {
    void (async () => {
      const hydrated = await attemptSessionHydrate();
      if (hydrated) {
        console.log(
          "[auth-error-handler] Hydrated session from cookie; skipping logout"
        );
        return;
      }
      clearAuthAndRedirect(redirectTo, { clearServerSession: false });
    })();
    return true;
  }

  clearAuthAndRedirect(redirectTo, {
    clearServerSession: isConfirmedInvalidSessionError(error),
  });
  return true;
}

/**
 * Async variant used by React Query / apiFetch when a 401 may be recoverable
 * via cookie hydrate (new tab with empty sessionStorage).
 */
export async function handleAuthErrorAsync(
  error: Error | string | unknown,
  redirectTo?: string
): Promise<boolean> {
  if (!isAuthError(error) && !isHttpSessionAuthError(error)) {
    return false;
  }

  const errorMessage = errorMessageOf(error);
  console.log(
    "[auth-error-handler] Authentication error (async) detected:",
    errorMessage
  );

  if (isLocalTokenMissingError(error) || isHttpSessionAuthError(error)) {
    const hydrated = await attemptSessionHydrate();
    if (hydrated) {
      console.log(
        "[auth-error-handler] Hydrated session from cookie; skipping logout"
      );
      return true;
    }
  }

  clearAuthAndRedirect(redirectTo, {
    clearServerSession: isConfirmedInvalidSessionError(error),
  });
  return true;
}

/**
 * Custom error class for authentication errors
 * Makes it easier to identify and handle auth errors explicitly
 */
export class AuthenticationError extends Error {
  constructor(message: string = "No authentication token found") {
    super(message);
    this.name = "AuthenticationError";
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthenticationError);
    }
  }
}

/**
 * If response is 401 or 403, parse body and throw AuthenticationError or PermissionError.
 * Call only when !response.ok and status is 401 or 403.
 * 401: attempt cookie hydrate first; only clear server session when JWT is confirmed invalid.
 * 403: keep session (permission denied).
 */
export async function throwOnSessionOrPermissionResponse(
  response: Response
): Promise<void> {
  let errorMessage =
    response.status === 403
      ? "You do not have permission to perform this action"
      : "Unauthorized";
  try {
    const errorData = await response.clone().json();
    errorMessage = errorData.error || errorData.message || errorMessage;
  } catch {
    /* ignore JSON parse errors */
  }
  if (response.status === 401) {
    const hydrated = await attemptSessionHydrate();
    if (hydrated) {
      throw new AuthenticationError(
        "Session was restored; please retry the request"
      );
    }
    clearAuthAndRedirect(undefined, {
      clearServerSession: isConfirmedInvalidSessionError(errorMessage),
    });
    throw new AuthenticationError(errorMessage);
  }
  if (response.status === 403) {
    throw new PermissionError(errorMessage);
  }
}

/** Permission denied (403) — session stays valid; do not clear auth or redirect to login. */
export class PermissionError extends Error {
  constructor(message: string = "You do not have permission to perform this action") {
    super(message);
    this.name = "PermissionError";
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, PermissionError);
    }
  }
}
